import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { 
  Activity, 
  Heart, 
  Brain, 
  Moon, 
  TrendingUp,
  Zap,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react'

interface HealthMetric {
  id: string
  name: string
  value: number
  unit: string
  trend: 'up' | 'down' | 'stable'
  color: string
  icon: React.ElementType
}

interface InteractiveDemoProps {
  className?: string
}

const InteractiveDemo: React.FC<InteractiveDemoProps> = ({ className = "" }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentMetric, setCurrentMetric] = useState(0)
  const [metrics, setMetrics] = useState<HealthMetric[]>([
    {
      id: 'heart-rate',
      name: 'Heart Rate',
      value: 72,
      unit: 'bpm',
      trend: 'stable',
      color: 'text-red-500',
      icon: Heart
    },
    {
      id: 'steps',
      name: 'Daily Steps',
      value: 8543,
      unit: 'steps',
      trend: 'up',
      color: 'text-blue-500',
      icon: Activity
    },
    {
      id: 'sleep',
      name: 'Sleep Quality',
      value: 85,
      unit: '%',
      trend: 'up',
      color: 'text-purple-500',
      icon: Moon
    },
    {
      id: 'stress',
      name: 'Stress Level',
      value: 32,
      unit: 'score',
      trend: 'down',
      color: 'text-green-500',
      icon: Brain
    }
  ])

  const [aiInsights] = useState<string[]>([
    "Your heart rate is within optimal range. Keep up the good work!",
    "Daily steps are trending upward. Consider increasing your target.",
    "Sleep quality has improved by 12% this week.",
    "Stress levels are decreasing. Meditation is working well."
  ])

  // Simulate real-time data updates
  useEffect(() => {
    if (!isPlaying) return

    const interval = setInterval(() => {
      setMetrics(prev => prev.map(metric => {
        let newValue = metric.value
        let newTrend = metric.trend

        // Simulate realistic fluctuations
        switch (metric.id) {
          case 'heart-rate':
            newValue = Math.max(60, Math.min(100, metric.value + (Math.random() - 0.5) * 4))
            newTrend = newValue > metric.value ? 'up' : newValue < metric.value ? 'down' : 'stable'
            break
          case 'steps':
            newValue = Math.max(0, metric.value + Math.floor((Math.random() - 0.3) * 20))
            newTrend = newValue > metric.value ? 'up' : 'down'
            break
          case 'sleep':
            newValue = Math.max(70, Math.min(100, metric.value + (Math.random() - 0.5) * 2))
            newTrend = newValue > metric.value ? 'up' : newValue < metric.value ? 'down' : 'stable'
            break
          case 'stress':
            newValue = Math.max(0, Math.min(100, metric.value + (Math.random() - 0.5) * 3))
            newTrend = newValue > metric.value ? 'up' : newValue < metric.value ? 'down' : 'stable'
            break
        }

        return {
          ...metric,
          value: Math.round(newValue),
          trend: newTrend
        }
      }))

      // Cycle through metrics for display
      setCurrentMetric(prev => (prev + 1) % metrics.length)
    }, 2000)

    return () => clearInterval(interval)
  }, [isPlaying, metrics.length])

  const toggleDemo = () => {
    setIsPlaying(!isPlaying)
  }

  const resetDemo = () => {
    setIsPlaying(false)
    setMetrics([
      {
        id: 'heart-rate',
        name: 'Heart Rate',
        value: 72,
        unit: 'bpm',
        trend: 'stable',
        color: 'text-red-500',
        icon: Heart
      },
      {
        id: 'steps',
        name: 'Daily Steps',
        value: 8543,
        unit: 'steps',
        trend: 'up',
        color: 'text-blue-500',
        icon: Activity
      },
      {
        id: 'sleep',
        name: 'Sleep Quality',
        value: 85,
        unit: '%',
        trend: 'up',
        color: 'text-purple-500',
        icon: Moon
      },
      {
        id: 'stress',
        name: 'Stress Level',
        value: 32,
        unit: 'score',
        trend: 'down',
        color: 'text-green-500',
        icon: Brain
      }
    ])
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'down':
        return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
      default:
        return <div className="w-4 h-4 bg-blue-400 rounded-full" />
    }
  }

  return (
    <div className={`${className}`}>
      <Card className="bg-theme-surface/80 border border-blue-200/50 dark:border-blue-700/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-theme-primary">Live Health Demo</CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                onClick={toggleDemo}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                <span>{isPlaying ? 'Pause' : 'Start'}</span>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={resetDemo}
                className="flex items-center space-x-2 bg-blue-100/50 hover:bg-blue-200 text-blue-700 border border-blue-300"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Current Metric Display */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMetric}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              <div className="flex items-center justify-between p-4 bg-theme-surface/70 rounded-xl border border-blue-200/30 dark:border-blue-700/30">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-lg bg-theme-surface/70 border border-blue-200/20 dark:border-blue-700/20 ${metrics[currentMetric].color}`}>
                    {React.createElement(metrics[currentMetric].icon, { className: "w-6 h-6" })}
                  </div>
                  <div>
                    <h3 className="font-semibold text-theme-primary">{metrics[currentMetric].name}</h3>
                    <p className="text-2xl font-bold text-theme-primary">
                      {metrics[currentMetric].value}
                      <span className="text-sm text-theme-muted ml-1">{metrics[currentMetric].unit}</span>
                    </p>
                  </div>
                </div>
                <div className={`flex items-center space-x-1 ${
                  metrics[currentMetric].trend === 'up' ? 'text-green-600 dark:text-green-400' :
                  metrics[currentMetric].trend === 'down' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
                }`}>
                  {getTrendIcon(metrics[currentMetric].trend)}
                  <span className="text-sm font-medium capitalize">{metrics[currentMetric].trend}</span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* All Metrics Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.id}
                whileHover={{ scale: 1.02 }}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  index === currentMetric 
                    ? 'border-blue-400 bg-blue-500/20 shadow-md' 
                    : 'border-theme-border bg-theme-surface/50 hover:bg-theme-surface/70 hover:border-theme-primary'
                }`}
                onClick={() => setCurrentMetric(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded bg-theme-surface/70 border border-blue-200/20 dark:border-blue-700/20 ${metric.color}`}>
                      {React.createElement(metric.icon, { className: "w-4 h-4" })}
                    </div>
                    <div>
                      <p className="text-xs text-theme-muted">{metric.name}</p>
                      <p className="font-semibold text-theme-primary">
                        {metric.value}{metric.unit}
                      </p>
                    </div>
                  </div>
                  <div className={metric.color}>
                    {getTrendIcon(metric.trend)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* AI Insights */}
          <div className="p-4 bg-theme-surface/70 rounded-xl border border-blue-200/30 dark:border-blue-700/30">
            <div className="flex items-center space-x-2 mb-3">
              <Zap className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              <h4 className="font-semibold text-theme-primary">AI Insights</h4>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse" />
                <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={currentMetric}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="text-sm text-theme-secondary leading-relaxed"
              >
                {aiInsights[currentMetric]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Progress Indicators */}
          <div className="mt-4 flex justify-center space-x-2">
            {metrics.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentMetric(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentMetric ? 'bg-blue-600 w-6' : 'bg-blue-300 hover:bg-blue-400 dark:bg-blue-700 dark:hover:bg-blue-600'
                }`}
                aria-label={`View ${metrics[index].name} metric`}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InteractiveDemo
